import Box from '@mui/material/Box';
import { useEffect, useState, Activity } from 'react';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';

import ProgramChart from './ProgramChart';
import LanguageChart from './LanguageChart';
import AgeChart from './AgeChart';
import DetailedChart from './DetailedChart';
import TypeChart from './TypeChart';
import InsuranceChart from './InsuranceChart';
import CloseoutReasonChart from './CloseoutReasonChart';
import OtherGauges from './OtherGauges';
import HudsonValleyMap from './counties/HudsonVallyMap';

const LOCATION = 'location';
const CLINIC = 'clinic';

const isAnySelected = (data) =>
  Object.values(data).some((facility) => Boolean(facility));

export default function ChipsAndCharts({ chartData, userType }) {
  const userTypes = {
    isAdmin: userType === 0,
    isLocation: userType === 1,
    isClinic: userType === 2,
    isPeer: userType === 3,
  };
  const [selectedFacilities, setSelectedFacilities] = useState({});
  useEffect(() => {
    const initFacilities = {};
    for (const {
      existingClinic,
      facilityContact: { facilityContactOrganization },
    } of chartData) {
      if (existingClinic) {
        if (!(existingClinic in initFacilities)) {
          initFacilities[existingClinic] = false;
        }
      }
      if (!(facilityContactOrganization in initFacilities)) {
        initFacilities[facilityContactOrganization] = false;
      }
    }
    setSelectedFacilities(initFacilities);
  }, []);

  const filterChartData = () => {
    const isAnyFacilitySelected = isAnySelected(selectedFacilities);
    if (!isAnyFacilitySelected) return chartData;
    return chartData.filter(
      ({
        existingClinic,
        facilityContact: { facilityContactOrganization },
      }) => {
        return (
          (facilityContactOrganization in selectedFacilities &&
            selectedFacilities[facilityContactOrganization]) ||
          (existingClinic in selectedFacilities &&
            selectedFacilities[existingClinic])
        );
      },
    );
  };

  const calculateCounts = (filteredData) => {
    const countFacilities = {};
    for (const {
      existingClinic,
      facilityContact: { facilityContactOrganization },
    } of filteredData) {
      if (existingClinic) {
        if (!(existingClinic in countFacilities)) {
          countFacilities[existingClinic] = {
            type: CLINIC,
            name: existingClinic,
            count: 1,
          };
        } else {
          countFacilities[existingClinic].count += 1;
        }
      }
      if (!(facilityContactOrganization in countFacilities)) {
        countFacilities[facilityContactOrganization] = {
          type: LOCATION,
          name: facilityContactOrganization,
          count: 1,
        };
      } else {
        countFacilities[facilityContactOrganization].count += 1;
      }
    }
    return countFacilities;
  };

  const filteredChartData = filterChartData();
  const facilityCounts = calculateCounts(filteredChartData);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '15px',
          flexWrap: 'wrap',
        }}
      >
        <Activity mode={userTypes.isLocation ? 'hidden' : 'visible'}>
          {Object.values(facilityCounts)
            .filter((facility) => facility.type === LOCATION)
            .map(({ name, count }) => {
              return (
                <Chip
                  key={name}
                  label={`${name}: ${count}`}
                  color={selectedFacilities[name] ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    // Only allow 1 selection
                    // at a time.
                    const isAnyFacilitySelected =
                      isAnySelected(selectedFacilities);
                    if (isAnyFacilitySelected && !selectedFacilities[name])
                      return;
                    setSelectedFacilities((prev) => ({
                      ...prev,
                      [name]: !prev[name],
                    }));
                  }}
                />
              );
            })}
        </Activity>
        <Activity
          mode={userTypes.isAdmin || userTypes.isPeer ? 'visible' : 'hidden'}
        >
          <Divider
            orientation='vertical'
            variant='middle'
            flexItem
            sx={{ borderWidth: 2 }}
          />
        </Activity>
        <Activity mode={userTypes.isClinic ? 'hidden' : 'visible'}>
          {Object.values(facilityCounts)
            .filter((facility) => facility.type === CLINIC)
            .map(({ name, count }) => {
              return (
                <Chip
                  key={name}
                  label={`${name}: ${count}`}
                  color={selectedFacilities[name] ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => {
                    const isAnyFacilitySelected =
                      isAnySelected(selectedFacilities);
                    if (isAnyFacilitySelected && !selectedFacilities[name])
                      return;
                    setSelectedFacilities((prev) => ({
                      ...prev,
                      [name]: !prev[name],
                    }));
                  }}
                />
              );
            })}
        </Activity>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: '5vw',
          flexWrap: 'wrap',
          width: '80vw',
        }}
      >
        <HudsonValleyMap chartData={filteredChartData} />
        <OtherGauges chartData={filteredChartData} />
        <CloseoutReasonChart chartData={filteredChartData} />
        <DetailedChart
          chartData={filteredChartData}
          isClinic={userType === 2}
        />
        <TypeChart chartData={filteredChartData} />
        <ProgramChart chartData={filteredChartData} />
        <LanguageChart chartData={filteredChartData} />
        <AgeChart chartData={filteredChartData} />
        <InsuranceChart chartData={filteredChartData} />
      </Box>
    </Box>
  );
}
